import {
  ConversationInput,
  MappedConversationMessage,
} from 'Assistant/__generated__/client.schemas';
import { HistoryErrorType, Message } from '../types';
import { ChatbotMessageRole } from '../enums/ChatbotMessageRole';
import { CommandMessage } from '../hooks';
import {
  isMessageContent,
  mapAssistantPayloadToSource,
} from './mapAssistantPayloadToSource';

type ConversationInputMessage = ConversationInput['message'];

export const mapHistoryToMessages = ({
  content,
  created_at,
  id,
  role,
  type,
}: MappedConversationMessage): Message[] => {
  const makeUser = (body: string): Message => ({
    id,
    createdAt: created_at,
    role: ChatbotMessageRole.user,
    content: body,
    topic: null,
    starterId: null,
    type,
  });

  if (role === ChatbotMessageRole.assistant) {
    const raw = Array.isArray(content) ? content : [content];
    const chunks = raw
      .map(mapAssistantPayloadToSource)
      .filter(
        (c): c is NonNullable<ReturnType<typeof mapAssistantPayloadToSource>> =>
          !!c && !(c.kind === 'text' && c.value === ''),
      );

    // If everything was empty / ignorable, return no messages
    if (chunks.length === 0) return [];

    // Single assistant message containing all chunk sources (one turn)
    return [
      {
        id,
        createdAt: created_at,
        role: ChatbotMessageRole.assistant,
        source: chunks,
        type,
      },
    ];
  }

  if (role === ChatbotMessageRole.user) {
    const body = isMessageContent(content)
      ? content.body
      : JSON.stringify(content);

    return [makeUser(body)];
  }

  return [];
};

// Backward-compatible single-message mapper (first chunk)
export const mapHistoryToMessage = (m: MappedConversationMessage): Message =>
  mapHistoryToMessages(m)[0];

function isCommandMessage(
  message: Message | CommandMessage,
): message is CommandMessage {
  return 'subtype' in message;
}

export const mapMessageToInput = (
  message: Message | CommandMessage,
): ConversationInputMessage => {
  if (isCommandMessage(message)) {
    if (message.subtype === 'url_prompt') {
      return {
        ...message,
        type_discriminator: `${message.type}:${message.subtype}`,
      };
    }

    return message;
  }

  if (message.role === ChatbotMessageRole.user) {
    if (message.starterId) {
      return {
        value: message.content,
        starter_id: message.starterId,
        type: 'conversation_starter',
      };
    }

    return { value: message.content, type: 'text' };
  }

  return { value: String(message.source), type: 'text' };
};

export const mapHistoryErrorType = (
  fetchHistoryError: boolean,
  featureUnavailable: boolean,
  userSessionExpired: boolean,
): HistoryErrorType => {
  if (userSessionExpired) return 'USER_SESSION_EXPIRED';
  if (featureUnavailable) return 'FEATURE_UNAVAILABLE';
  if (fetchHistoryError) return 'RETRY';

  return null;
};
